import { useEffect, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, ClipboardList, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { useWorkflowReport } from '@/hooks/useWorkflowReport';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/contexts/AuthContext';
import { useInspectionCapabilities } from '@/hooks/useInspectionCapabilities';
import { WorkflowEventsSection } from './workflow/WorkflowEventsSection';
import { WorkflowComplaintsSection } from './workflow/WorkflowComplaintsSection';
import { WorkflowNotesSection } from './workflow/WorkflowNotesSection';
import { WorkflowInspectionSection } from './workflow/WorkflowInspectionSection';
import { WorkflowDiagnosticsSection } from './workflow/WorkflowDiagnosticsSection';
import { WorkflowCommunicationsSection } from './workflow/WorkflowCommunicationsSection';
import { ShowFeature } from '@/components/access/ShowFeature';
import { TechnicianFindingsPanel } from './DiagnosticEvidence';
import type { Ticket } from '@/types';

interface WorkflowReportTabProps {
  ticket: Ticket;
  className?: string;
  /** Deep link (Oct 20 Issue 06): bring a section into view once the report has loaded. */
  focusSection?: 'notes';
  /** Changes on every deep-link request, so a second tap on the card's Notes shortcut scrolls again. */
  focusRequest?: number;
}

export function WorkflowReportTab({ ticket, className, focusSection, focusRequest }: WorkflowReportTabProps) {
  const shopId = (ticket as any).shop_id || (ticket as any).shopId;
  const {
    report, complaints, notes, noteChain, inspections, diagnostics, isLoading, loadFailures, retryLoad,
    addComplaint, updateComplaint, addNote, updateNote, deleteNote,
    startInspection, saveInspectionItems, completeInspection,
    createDiagnostic, updateDiagnostic, startDiagnostic, completeDiagnostic,
    addTest, updateTest, removeTest,
  } = useWorkflowReport(ticket.id, shopId);

  const { hasRole } = usePermissions();
  const capabilities = useInspectionCapabilities(shopId);

  const canEditComplaints = hasRole('SERVICE_WRITER', 'MANAGER', 'ORG_ADMIN');
  const canEditInspections = hasRole('TECHNICIAN', 'MANAGER', 'ORG_ADMIN');
  const canEditDiagnostics = hasRole('TECHNICIAN', 'MANAGER', 'ORG_ADMIN');
  // Notes (FD-10): everyone with ticket access ADDS notes; a note is changed by its author, or corrected by a
  // Manager/Owner (multi-role aware, the same authority the server checks in edit_workflow_note).
  const { user } = useAuth();
  const noteViewer = { userId: user?.id, canCorrectOthers: hasRole('OWNER', 'MANAGER') };

  // Deep link: once loaded, scroll Internal Notes to the top of the tab and move keyboard focus to its header.
  const notesRef = useRef<HTMLDivElement>(null);
  const notesFocus = focusSection === 'notes' ? (focusRequest ?? 1) : 0;
  useEffect(() => {
    if (!notesFocus || isLoading) return;
    const bring = () => {
      const el = notesRef.current;
      if (!el) return;
      el.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
      el.querySelector<HTMLElement>('[data-testid="workflow-notes-header"]')?.focus({ preventScroll: true });
    };
    const raf = requestAnimationFrame(bring);
    // The events list above loads on its own; settle once more so it cannot push Notes back out of view.
    const late = setTimeout(bring, 450);
    return () => { cancelAnimationFrame(raf); clearTimeout(late); };
  }, [notesFocus, isLoading]);

  const latestComplaint = complaints.length > 0 ? complaints[complaints.length - 1].text : undefined;

  if (isLoading) {
    return (
      <div className={className}>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className={className}>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Workflow Report</h2>
          </div>
          {report && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Updated {format(new Date(report.updated_at), 'MMM d, h:mm a')}
            </span>
          )}
        </div>

        {/* Quick chips */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">{ticket.status}</Badge>
          {(ticket as any).technician_name && <Badge variant="secondary">{(ticket as any).technician_name}</Badge>}
          {(ticket as any).bay_name && <Badge variant="secondary">Bay: {(ticket as any).bay_name}</Badge>}
        </div>

        {loadFailures.length > 0 && (
          <div
            role="alert"
            data-testid="workflow-report-load-failure"
            className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm"
          >
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="flex-1">
              Could not load: {loadFailures.join(', ')}. What is shown may be incomplete — empty does not mean none recorded.
            </span>
            <Button size="sm" variant="outline" onClick={retryLoad}>Retry</Button>
          </div>
        )}

        {/* Sections */}
        <WorkflowEventsSection ticketId={ticket.id} />

        <WorkflowComplaintsSection
          complaints={complaints}
          canEdit={canEditComplaints}
          onAdd={(text) => addComplaint.mutate(text)}
          onUpdate={(id, text) => updateComplaint.mutate({ id, text })}
          shopId={shopId}
        />

        <WorkflowNotesSection
          ref={notesRef}
          notes={notes}
          chain={noteChain}
          viewer={noteViewer}
          focusRequest={notesFocus}
          onAdd={(noteText) => addNote.mutate(noteText)}
          onUpdate={(id, noteText, reason, noteTicketId) => updateNote.mutate({ id, noteText, reason, noteTicketId })}
          onDelete={(id, reason, noteTicketId) => deleteNote.mutate({ id, reason, noteTicketId })}
          shopId={shopId}
        />

        <ShowFeature feature="ui_workflowInspections">
          <WorkflowInspectionSection
            inspections={inspections}
            canEdit={canEditInspections}
            onStart={() => startInspection.mutate()}
            onSave={(inspectionId, items) => saveInspectionItems.mutate({ inspectionId, items })}
            onComplete={(inspectionId) => completeInspection.mutate(inspectionId)}
            shopId={shopId}
            ticketId={ticket.id}
            capabilities={capabilities}
          />
        </ShowFeature>

        {/* OCT28 #2: technicians' findings + recommended repairs, visible to the front office (not feature-gated). */}
        <TechnicianFindingsPanel ticketId={ticket.id} />

        <ShowFeature feature="ui_workflowDiagnostics">
          <WorkflowDiagnosticsSection
            diagnostics={diagnostics}
            canEdit={canEditDiagnostics}
            latestComplaint={latestComplaint}
            onCreate={(concern) => createDiagnostic.mutate(concern)}
            onUpdate={(id, updates) => updateDiagnostic.mutate({ id, updates })}
            onStart={(id) => startDiagnostic.mutate(id)}
            onComplete={(id) => completeDiagnostic.mutate(id)}
            onAddTest={(diagnosticId, testName) => addTest.mutate({ diagnosticId, testName })}
            onUpdateTest={(id, updates) => updateTest.mutate({ id, updates })}
            onRemoveTest={(id) => removeTest.mutate(id)}
            shopId={shopId}
          />
        </ShowFeature>

        <ShowFeature feature="ui_workflowCustomerComms">
          <WorkflowCommunicationsSection />
        </ShowFeature>
      </div>
    </ScrollArea>
  );
}
